import { wOrgIdMiddleware } from "../utils/commonUtils";
import { OK } from "../utils/constant";
import http from "./http";

const RxntService = {
  // Settings
  getSettings: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.GET(`${url}rxnt/settings`);
      return response;
    } catch (error) {
      console.error("RXNT getSettings error", error);
      throw error;
    }
  }),

  updateSettings: wOrgIdMiddleware(async (url, data) => {
    try {
      const response = await http.POST(`${url}rxnt/settings`, data);
      return response;
    } catch (error) {
      console.error("RXNT updateSettings error", error);
      throw error;
    }
  }),

  testConnection: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.POST(`${url}rxnt/test-connection`);
      return response;
    } catch (error) {
      console.error("RXNT testConnection error", error);
      throw error;
    }
  }),

  /**
   * RXNT Integration SDK — access token for authFactory.
   * Server uses RXNT_CLIENT_ID/SECRET; browser never sees credentials.
   */
  getLaunchToken: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.GET(`${url}rxnt/launch/token`);
      return response;
    } catch (error) {
      console.error("RXNT getLaunchToken error", error);
      throw error;
    }
  }),

  /**
   * RXNT Integration SDK — launchData for launchDataFactory.
   * @param {object} params provider_id, rxnt_doctor_id, employee_id, rxnt_patient_id, return_url, …
   */
  getLaunchContext: wOrgIdMiddleware(async (url, params = {}) => {
    try {
      const p = params && typeof params === "object" ? params : {};
      const qs = new URLSearchParams();
      if (p.provider_id) qs.set("provider_id", p.provider_id);
      if (p.rxnt_doctor_id != null && p.rxnt_doctor_id !== "") {
        qs.set("rxnt_doctor_id", String(p.rxnt_doctor_id));
      }
      if (p.employee_id) qs.set("employee_id", p.employee_id);
      if (p.rxnt_patient_id != null && p.rxnt_patient_id !== "") {
        qs.set("rxnt_patient_id", String(p.rxnt_patient_id));
      }
      if (p.return_url) qs.set("return_url", p.return_url);
      if (p.external_order_id) qs.set("external_order_id", p.external_order_id);
      if (p.activate_patient_portal != null) {
        qs.set("activate_patient_portal", p.activate_patient_portal ? "1" : "0");
      }
      if (p.hm != null) {
        qs.set("hm", p.hm ? "1" : "0");
      }
      const q = qs.toString();
      const response = await http.GET(`${url}rxnt/launch/context${q ? `?${q}` : ""}`);
      return response;
    } catch (error) {
      console.error("RXNT getLaunchContext error", error);
      throw error;
    }
  }),

  // Locations
  getLocations: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.GET(`${url}rxnt/locations`);
      return response;
    } catch (error) {
      console.error("RXNT getLocations error", error);
      throw error;
    }
  }),

  getMappedLocations: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.GET(`${url}rxnt/locations/mapped`);
      return response;
    } catch (error) {
      console.error("RXNT getMappedLocations error", error);
      throw error;
    }
  }),

  mapLocation: wOrgIdMiddleware(async (url, clinicId, rxntLocationId) => {
    try {
      const response = await http.POST(`${url}rxnt/locations/map`, {
        clinic_id: clinicId,
        rxnt_location_id: rxntLocationId,
      });
      return response;
    } catch (error) {
      console.error("RXNT mapLocation error", error);
      throw error;
    }
  }),

  unmapLocation: wOrgIdMiddleware(async (url, clinicId) => {
    try {
      const response = await http.DELETE(`${url}rxnt/locations/unmap/${clinicId}`);
      return response;
    } catch (error) {
      console.error("RXNT unmapLocation error", error);
      throw error;
    }
  }),

  // Providers
  getProviders: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.GET(`${url}rxnt/providers`);
      return response;
    } catch (error) {
      console.error("RXNT getProviders error", error);
      throw error;
    }
  }),

  getAvailableRxntDoctors: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.GET(`${url}rxnt/providers/rxnt-doctors`);
      return response;
    } catch (error) {
      console.error("RXNT getAvailableRxntDoctors error", error);
      throw error;
    }
  }),

  mapProvider: wOrgIdMiddleware(async (url, providerId, rxntDoctorId) => {
    try {
      const response = await http.POST(`${url}rxnt/providers/map`, {
        provider_id: providerId,
        rxnt_doctor_id: rxntDoctorId,
      });
      return response;
    } catch (error) {
      console.error("RXNT mapProvider error", error);
      throw error;
    }
  }),

  unmapProvider: wOrgIdMiddleware(async (url, providerId) => {
    try {
      const response = await http.DELETE(`${url}rxnt/providers/unmap/${providerId}`);
      return response;
    } catch (error) {
      console.error("RXNT unmapProvider error", error);
      throw error;
    }
  }),

  syncProviders: wOrgIdMiddleware(async (url) => {
    try {
      const response = await http.POST(`${url}rxnt/providers/sync`);
      return response;
    } catch (error) {
      console.error("RXNT syncProviders error", error);
      throw error;
    }
  }),

  getProvidersByClinic: wOrgIdMiddleware(async (url, clinicId) => {
    try {
      const response = await http.GET(`${url}rxnt/providers/by-clinic/${clinicId}`);
      return response;
    } catch (error) {
      console.error("RXNT getProvidersByClinic error", error);
      throw error;
    }
  }),

  // Search APIs
  searchPharmacies: wOrgIdMiddleware(async (url, params = {}) => {
    try {
      const p =
        typeof params === "string"
          ? { search: params }
          : params && typeof params === "object"
            ? params
            : {};
      const qs = new URLSearchParams();
      if (p.search) qs.set("search", p.search);
      if (p.name) qs.set("name", p.name);
      if (p.address) qs.set("address", p.address);
      if (p.city) qs.set("city", p.city);
      if (p.state) qs.set("state", p.state);
      if (p.zip) qs.set("zip", p.zip);
      if (p.phone) qs.set("phone", p.phone);
      if (p.fax) qs.set("fax", p.fax);
      if (p.page != null && p.page !== "") qs.set("page", String(p.page));
      if (p.size != null && p.size !== "") qs.set("size", String(p.size));
      if (p.sortBy) qs.set("sortBy", p.sortBy);
      if (p.sortOrder) qs.set("sortOrder", p.sortOrder);
      const q = qs.toString();
      const response = await http.GET(
        `${url}rxnt/pharmacies${q ? `?${q}` : ""}`
      );
      return response;
    } catch (error) {
      console.error("RXNT searchPharmacies error", error);
      throw error;
    }
  }),

  searchMedications: wOrgIdMiddleware(async (url, params = {}) => {
    try {
      const p =
        typeof params === "string"
          ? { search: params }
          : params && typeof params === "object"
            ? params
            : {};
      const qs = new URLSearchParams();
      if (p.search) qs.set("search", p.search);
      if (p.page != null && p.page !== "") qs.set("page", String(p.page));
      if (p.size != null && p.size !== "") qs.set("size", String(p.size));
      const q = qs.toString();
      const response = await http.GET(`${url}rxnt/medications${q ? `?${q}` : ""}`);
      return response;
    } catch (error) {
      console.error("RXNT searchMedications error", error);
      throw error;
    }
  }),

  getDrugDetails: wOrgIdMiddleware(async (url, drugId) => {
    try {
      const response = await http.GET(`${url}rxnt/medications/${drugId}`);
      return response;
    } catch (error) {
      console.error("RXNT getDrugDetails error", error);
      throw error;
    }
  }),

  // Prescriptions
  createPrescription: wOrgIdMiddleware(async (url, data) => {
    try {
      const response = await http.POST(`${url}rxnt/prescriptions`, data);
      return response;
    } catch (error) {
      console.error("RXNT createPrescription error", error);
      throw error;
    }
  }),

  listPrescriptions: wOrgIdMiddleware(async (url, params = {}) => {
    try {
      const p = params && typeof params === "object" ? params : {};
      const qs = new URLSearchParams();
      if (p.tab) qs.set("tab", p.tab);
      if (p.provider_id) qs.set("provider_id", p.provider_id);
      if (p.rxnt_doctor_id != null && p.rxnt_doctor_id !== "") {
        qs.set("rxnt_doctor_id", String(p.rxnt_doctor_id));
      }
      if (p.per_page != null && p.per_page !== "") {
        qs.set("per_page", String(p.per_page));
      }
      const q = qs.toString();
      const response = await http.GET(
        `${url}rxnt/prescriptions${q ? `?${q}` : ""}`
      );
      return response;
    } catch (error) {
      console.error("RXNT listPrescriptions error", error);
      throw error;
    }
  }),

  getPrescriptions: wOrgIdMiddleware(async (url, visitId) => {
    try {
      const response = await http.GET(`${url}rxnt/prescriptions/visit/${visitId}`);
      return response;
    } catch (error) {
      console.error("RXNT getPrescriptions error", error);
      throw error;
    }
  }),

  getPrescriptionHistory: wOrgIdMiddleware(async (url, employeeId, limit = 500) => {
    try {
      const qs =
        limit != null && limit !== ''
          ? `?limit=${encodeURIComponent(String(limit))}`
          : '';
      const response = await http.GET(`${url}rxnt/prescriptions/history/${employeeId}${qs}`);
      return response;
    } catch (error) {
      console.error("RXNT getPrescriptionHistory error", error);
      throw error;
    }
  }),

  getPrescription: wOrgIdMiddleware(async (url, prescriptionId) => {
    try {
      const response = await http.GET(`${url}rxnt/prescriptions/${prescriptionId}`);
      return response;
    } catch (error) {
      console.error("RXNT getPrescription error", error);
      throw error;
    }
  }),

  voidPrescription: wOrgIdMiddleware(async (url, prescriptionId, payload) => {
    try {
      let comments;
      let voidCode;
      if (typeof payload === 'string') {
        comments = payload;
        voidCode = 'NONE';
      } else {
        comments = payload?.comments ?? payload?.reason;
        voidCode = payload?.voidCode ?? 'NONE';
      }
      const response = await http.POST(`${url}rxnt/prescriptions/${prescriptionId}/void`, {
        comments,
        voidCode,
      });
      return response;
    } catch (error) {
      console.error("RXNT voidPrescription error", error);
      throw error;
    }
  }),

  refillPrescription: wOrgIdMiddleware(async (url, prescriptionId) => {
    try {
      const response = await http.POST(`${url}rxnt/prescriptions/${prescriptionId}/refill`);
      return response;
    } catch (error) {
      console.error("RXNT refillPrescription error", error);
      throw error;
    }
  }),

  reinstatePrescription: wOrgIdMiddleware(async (url, prescriptionId) => {
    try {
      const response = await http.POST(`${url}rxnt/prescriptions/${prescriptionId}/reinstate`);
      return response;
    } catch (error) {
      console.error("RXNT reinstatePrescription error", error);
      throw error;
    }
  }),

  printPrescription: wOrgIdMiddleware(async (url, prescriptionId) => {
    try {
      const response = await http.GET(`${url}rxnt/prescriptions/${prescriptionId}/print`);
      return response;
    } catch (error) {
      console.error("RXNT printPrescription error", error);
      throw error;
    }
  }),

  /** Print by RXNT prescription id + doctor id (gateway worklist rows without a local Snap record). */
  printPrescriptionGateway: wOrgIdMiddleware(async (url, rxntPrescriptionId, rxntDoctorId) => {
    try {
      const qs = new URLSearchParams({
        rxnt_prescription_id: String(rxntPrescriptionId),
        rxnt_doctor_id: String(rxntDoctorId),
      });
      const response = await http.GET(`${url}rxnt/prescriptions/gateway-print?${qs.toString()}`);
      return response;
    } catch (error) {
      console.error("RXNT printPrescriptionGateway error", error);
      throw error;
    }
  }),

  // API Logs
  getApiLogs: wOrgIdMiddleware(async (url, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await http.GET(`${url}rxnt/logs?${queryString}`);
      return response;
    } catch (error) {
      console.error("RXNT getApiLogs error", error);
      throw error;
    }
  }),
};

export default RxntService;
